import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  InputAdornment
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { RaspberryPi } from '../model/RaspberryPi';
import { PiService } from '../service/piService';

interface RaspberryPiDetailProps {
  pi: RaspberryPi;
  onBackClick: () => void;
  onUpdate: () => void;
}

// Status-Farbe basierend auf dem Status ermitteln
const getStatusColor = (status: string): string => {
  switch (status) {
    case 'created':
      return 'info.main';
    case 'ready_for_shipping':
      return 'secondary.light';
    case 'out_for_delivery':
      return 'secondary.main';
    case 'installed':
      return 'primary.main';
    default:
      return 'grey.500';
  }
};

// Status-Text basierend auf dem Status ermitteln
const getStatusText = (status: string): string => {
  switch (status) {
    case 'created':
      return 'Erstellt';
    case 'ready_for_shipping':
      return 'Versandfertig';
    case 'out_for_delivery':
      return 'Unterwegs';
    case 'installed':
      return 'Installiert';
    default:
      return 'Unbekannt';
  }
};

const RaspberryPiDetail: React.FC<RaspberryPiDetailProps> = ({ pi, onBackClick, onUpdate }) => {
  // Stellen sicher, dass credentials immer definiert ist
  const initialPi: RaspberryPi = {
    ...pi,
    credentials: pi.credentials || {
      username: '',
      password: '',
      sshKey: ''
    }
  };
  
  const [editedPi, setEditedPi] = useState<RaspberryPi>(initialPi);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedPi(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCredentialsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedPi(prev => {
      const currentCredentials = prev.credentials || { username: '', password: '', sshKey: '' };
      
      return {
        ...prev,
        credentials: {
          ...currentCredentials,
          [name]: value
        }
      };
    });
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSnackbar('In die Zwischenablage kopiert', 'success');
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    // Überprüfe, ob die IP-Adresse gesetzt ist
    if (!editedPi.ipAddress || editedPi.ipAddress.trim() === '') {
      setError('Bitte geben Sie eine gültige IP-Adresse für den Raspberry Pi ein.');
      setIsLoading(false);
      return;
    }
    
    // Validiere das IP-Adressformat (einfache Validierung für IPv4)
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(editedPi.ipAddress)) {
      setError('Die eingegebene IP-Adresse hat ein ungültiges Format. Bitte verwenden Sie das Format xxx.xxx.xxx.xxx');
      setIsLoading(false);
      return;
    }
    
    try {
      // Wir filtern alle Felder heraus, die sich tatsächlich geändert haben
      const changedFields: Partial<RaspberryPi> = {};
      if (editedPi.name !== pi.name) changedFields.name = editedPi.name;
      if (editedPi.serialNumber !== pi.serialNumber) changedFields.serialNumber = editedPi.serialNumber;
      if (editedPi.ipAddress !== pi.ipAddress) changedFields.ipAddress = editedPi.ipAddress;
      if (editedPi.subdomain !== pi.subdomain) changedFields.subdomain = editedPi.subdomain;
      
      // Nur API-Aufruf machen, wenn es tatsächlich Änderungen gibt
      if (Object.keys(changedFields).length > 0) {
        await PiService.updatePi(pi._id, changedFields);
        setSuccess('Raspberry Pi wurde erfolgreich aktualisiert.');
        onUpdate();
      } else {
        setSuccess('Keine Änderungen vorgenommen.');
      }
    } catch (err: unknown) {
      setError('Fehler beim Speichern der Änderungen. Bitte versuchen Sie es später erneut.');
      console.error('Save error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Status-Update-Funktion
  const handleStatusChange = async (newStatus: 'created' | 'ready_for_shipping' | 'out_for_delivery' | 'installed') => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      await PiService.updatePiStatus(pi._id, newStatus);
      
      // Aktualisiere den lokalen Zustand
      setEditedPi(prev => ({
        ...prev,
        status: newStatus
      }));
      
      setSuccess(`Status wurde erfolgreich auf "${getStatusText(newStatus)}" geändert.`);
      onUpdate(); // Informiere die übergeordnete Komponente über die Aktualisierung
    } catch (err) {
      setError('Fehler beim Aktualisieren des Status. Bitte versuchen Sie es später erneut.');
      console.error('Status update error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      {/* Header mit Zurück-Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBackClick} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h2">
          {editedPi.name} Details
        </Typography>
      </Box>

      {/* Status-Anzeige */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ mr: 2 }}>{editedPi.name}</Typography>
        <Chip 
          label={getStatusText(editedPi.status)}
          sx={{ 
            backgroundColor: getStatusColor(editedPi.status),
            color: 'white'
          }}
        />
      </Box>

      {/* Erfolgs- oder Fehlermeldungen */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Status-Management - direkt ins Layout integriert */}
      <Tabs
        value={editedPi.status}
        onChange={(e, newValue) => {
          handleStatusChange(newValue as 'created' | 'ready_for_shipping' | 'out_for_delivery' | 'installed');
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ 
          mb: 4,
          '& .MuiTab-root': { 
            color: 'text.primary',
            fontWeight: 'medium',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            margin: '0 4px',
            minHeight: '48px',
          },
          '& .Mui-selected': {
            backgroundColor: (theme) => theme.palette.action.selected,
            fontWeight: 'bold',
          }
        }}
      >
        <Tab 
          label="Erstellt" 
          value="created" 
          sx={{ 
            py: 1,
            ...(editedPi.status === 'created' && {
              backgroundColor: 'info.light',
              borderColor: 'info.main',
            })
          }} 
        />
        <Tab 
          label="Versandfertig" 
          value="ready_for_shipping" 
          sx={{ 
            py: 1,
            ...(editedPi.status === 'ready_for_shipping' && {
              backgroundColor: 'secondary.light',
              borderColor: 'secondary.main',
            })
          }} 
        />
        <Tab 
          label="Unterwegs" 
          value="out_for_delivery" 
          sx={{ 
            py: 1,
            ...(editedPi.status === 'out_for_delivery' && {
              backgroundColor: 'secondary.light',
              borderColor: 'secondary.main',
            })
          }} 
        />
        <Tab 
          label="Installiert" 
          value="installed" 
          sx={{ 
            py: 1,
            ...(editedPi.status === 'installed' && {
              backgroundColor: 'primary.light',
              borderColor: 'primary.main',
            })
          }} 
        />
      </Tabs>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Allgemeine Informationen</Typography>
        <Grid container spacing={3}>
          {/* Seriennummer field */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Seriennummer"
              name="serialNumber"
              value={editedPi.serialNumber}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton onClick={() => handleCopyToClipboard(editedPi.serialNumber)}>
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
              variant="outlined"
              margin="normal"
            />
          </Grid>
          
          {/* Subdomain field */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Subdomain"
              name="subdomain"
              value={editedPi.subdomain || ''}
              onChange={handleInputChange}
              variant="outlined"
              margin="normal"
              disabled={!!editedPi.ownerId}
              InputProps={{
                readOnly: !!editedPi.ownerId,
                endAdornment: (
                  <IconButton onClick={() => handleCopyToClipboard(editedPi.subdomain || '')}>
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          
          {/* Waschstraßen-Zuweisung section */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              backgroundColor: editedPi.ownerName ? 'rgba(46, 125, 50, 0.1)' : 'rgba(0, 0, 0, 0.05)', 
              p: 2, 
              borderRadius: 1,
              mb: 2 
            }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Waschstraßen-Zuweisung
                </Typography>
                {editedPi.ownerName ? (
                  <Typography variant="body2">
                    Diesem Pi ist die Waschstraße <strong>{editedPi.ownerName}</strong> zugewiesen
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    Diesem Pi ist keine Waschstraße zugewiesen
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* WireGuard Konfiguration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>WireGuard Konfiguration</Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="WireGuard Port"
              value={editedPi.wireguardPort || 51820}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton onClick={() => handleCopyToClipboard(String(editedPi.wireguardPort || 51820))}>
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
              variant="outlined"
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Client Private Key"
              value={editedPi.wireguardClientPrivateKey || 'Nicht generiert'}
              InputProps={{
                readOnly: true,
                endAdornment: editedPi.wireguardClientPrivateKey && (
                  <IconButton onClick={() => handleCopyToClipboard(editedPi.wireguardClientPrivateKey || '')}>
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
              variant="outlined"
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Server Public Key"
              value={editedPi.wireguardServerPublicKey || 'Nicht generiert'}
              InputProps={{
                readOnly: true,
                endAdornment: editedPi.wireguardServerPublicKey && (
                  <IconButton onClick={() => handleCopyToClipboard(editedPi.wireguardServerPublicKey || '')}>
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
              variant="outlined"
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="WireGuard Preshared Key"
              value={editedPi.wireguardPresharedKey || 'Nicht generiert'}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        navigator.clipboard.writeText(editedPi.wireguardPresharedKey || '');
                        setSnackbarMessage('Preshared Key in die Zwischenablage kopiert');
                        setSnackbarOpen(true);
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Zugangsdaten</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Benutzername"
              name="username"
              value={editedPi.credentials?.username || ''}
              onChange={handleCredentialsChange}
              variant="outlined"
              margin="normal"
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => handleCopyToClipboard(editedPi.credentials?.username || '')}>
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Passwort"
              name="password"
              type="text"
              value={editedPi.credentials?.password || ''}
              onChange={handleCredentialsChange}
              variant="outlined"
              margin="normal"
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => handleCopyToClipboard(editedPi.credentials?.password || '')}>
                    <ContentCopyIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button 
          variant="outlined" 
          color="error" 
          startIcon={<DeleteIcon />}
          onClick={() => {
            if (window.confirm('Möchten Sie diesen Raspberry Pi wirklich löschen?')) {
              PiService.deletePi(pi._id)
                .then(() => {
                  showSnackbar('Raspberry Pi erfolgreich gelöscht', 'success');
                  onBackClick();
                })
                .catch((err: unknown) => {
                  console.error('Fehler beim Löschen:', err);
                  showSnackbar('Fehler beim Löschen des Raspberry Pi', 'error');
                });
            }
          }}
        >
          Löschen
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : "Speichern"}
        </Button>
      </Box>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RaspberryPiDetail; 